// musicianGrapher.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getSimilarMusicians, getTopTags } from '../utils/api';
import { isMobile } from '../utils/deviceUtils';
import { getConfig } from './graph/graphConfig';
import { generateMockMusician, generateMockSimilarMusicians, isMockData, extractMockNodeCount } from './graph/mockData';
import { createClusterGroups, generateHullPath } from './graph/clustering';
import { exportGraphAsPNG } from './graph/exportGraph';
import LoadingOverlay from './loadingOverlay';
import * as d3 from 'd3';

// --- Label width measurement (one shared canvas, cached across renders) ---
let measureContext = null;
const labelWidthCache = new Map();

const getLabelWidth = (text, fontSize) => {
    const key = `${text}|${fontSize}`;
    const cached = labelWidthCache.get(key);
    if (cached !== undefined) return cached;

    if (!measureContext) {
        measureContext = document.createElement('canvas').getContext('2d');
    }
    if (!measureContext) return text.length * fontSize * 0.6;

    measureContext.font = `${fontSize}px Arial`;
    const width = measureContext.measureText(text).width;
    labelWidthCache.set(key, width);
    return width;
};


export default function MusicianGraph({ initialMusicians, onMouseMove, onDownloadRequest, onMobileZoomRequest, onZoomTransform, onActivityChange, username }) {
    const [isLoading, setIsLoading] = useState(true);
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [clickedMusicians, setClickedMusicians] = useState([]);
    const mobile = isMobile();
    const config = getConfig(mobile);

    const nodeSize = config.nodeSize;
    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [mobileZoomLevel, setMobileZoomLevel] = useState(1);
    const svgRef = useRef();
    const currentZoomTransform = useRef(d3.zoomIdentity);
    const [processedMusicians, setProcessedMusicians] = useState(new Set());

    // Pending timers/frames of the current graph build, cleared on rebuild and unmount
    const scheduledRef = useRef({
        mouseMoveTimer: null,
        zoomFrame: null
    });

    const zoomRef = useRef(null);
    const simulationRef = useRef(null);
    const updateClusterShapesRef = useRef(null);

    // Memoize expensive calculations
    const memoizedCalculations = useMemo(() => {
        const opacityScale = d3.scaleLinear()
            .domain([0.8, 1.4])
            .range([0, 1])
            .clamp(true);

        const clusterForceScale = d3.scaleLinear()
            .domain([0.8, 1.4])
            .range([mobile ? 0.5 : 1, 0])
            .clamp(true);

        return { opacityScale, clusterForceScale };
    }, [mobile]);

    const graphStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 2,
        touchAction: mobile ? 'manipulation' : 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitUserDrag: 'none',
        KhtmlUserSelect: 'none'
    };

    // --- PNG export via extracted module ---
    const downloadSVGAsPNG = useCallback((withOverlay = true, returnCanvas = false) => {
        // Hull paths are not updated while hidden, so refresh them before cloning the svg
        updateClusterShapesRef.current?.();
        return exportGraphAsPNG(svgRef.current, withOverlay, returnCanvas, mobile);
    }, [mobile]);

    useEffect(() => {
        if (onDownloadRequest) {
            onDownloadRequest((withOverlay = true, returnCanvas = false) =>
                downloadSVGAsPNG(withOverlay, returnCanvas)
            );
        }
    }, [downloadSVGAsPNG]);

    const resizeTimerRef = useRef(null);
    const handleResize = useCallback(() => {
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = setTimeout(() => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        }, 250);
    }, []);

    const isValidNode = useCallback((node) => {
        return node && typeof node.id !== 'undefined' && node.id !== null;
    }, []);

    const filterSuperstrings = useCallback((graphData) => {
        if (!graphData?.nodes?.length) return { nodes: [], links: [] };

        const nodeIds = graphData.nodes
            .filter(isValidNode)
            .map(node => node.id)
            .sort((a, b) => a.length - b.length);

        const nodesToRemove = new Set();

        for (let i = 0; i < nodeIds.length; i++) {
            for (let j = i + 1; j < nodeIds.length; j++) {
                if (nodeIds[j]?.includes(nodeIds[i])) {
                    nodesToRemove.add(nodeIds[j]);
                }
            }
        }

        const filteredNodes = graphData.nodes.filter(node =>
            isValidNode(node) && !nodesToRemove.has(node.id)
        );

        const validNodeIds = new Set(filteredNodes.map(node => node.id));

        const filteredLinks = (graphData.links || []).filter(link => {
            if (!link?.source || !link?.target) return false;

            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;

            return validNodeIds.has(sourceId) && validNodeIds.has(targetId) &&
                !nodesToRemove.has(sourceId) && !nodesToRemove.has(targetId);
        });

        return {
            nodes: filteredNodes,
            links: filteredLinks
        };
    }, [isValidNode]);

    const fetchTagsForMusician = async (musician) => {
        if (!musician || typeof musician !== 'string' || musician.trim() === '') {
            console.warn('Invalid musician name provided:', musician);
            return null;
        }

        try {
            if (processedMusicians.has(musician)) {
                const existingNode = graphData.nodes.find(n => n?.id === musician);
                return existingNode || { id: musician, tags: [] };
            }

            const tags = await getTopTags(musician);
            setProcessedMusicians(prev => new Set([...prev, musician]));
            return { id: musician, tags: tags || [] };
        } catch (error) {
            console.error(`Error fetching tags for ${musician}:`, error);
            return { id: musician, tags: [] };
        }
    };

    // --- Data fetching: initial load ---
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!initialMusicians?.length) {
                setIsLoading(false);
                return;
            }

            // Handle mock data
            if (isMockData(initialMusicians)) {
                const nodeCount = extractMockNodeCount(initialMusicians[0]);
                console.log(`Generating mock data with ${nodeCount} nodes`);

                try {
                    setIsLoading(true);

                    const mockCentralNode = generateMockMusician('mock_central');
                    const remainingNodes = nodeCount - 1;

                    const mockSimilarMusicians = [];
                    const mockNodes = [mockCentralNode];
                    const mockLinks = [];

                    for (let i = 1; i <= remainingNodes; i++) {
                        const mockNode = generateMockMusician(`mock_node${i}`);
                        mockNodes.push(mockNode);
                        mockSimilarMusicians.push({
                            name: `mock_node${i}`,
                            match: (Math.random() * 0.5 + 0.5).toFixed(3)
                        });
                    }

                    mockSimilarMusicians.forEach(artist => {
                        mockLinks.push({
                            source: 'mock_central',
                            target: artist.name,
                            strength: parseFloat(artist.match)
                        });
                    });

                    const additionalConnections = Math.floor(remainingNodes * 0.3);
                    for (let i = 0; i < additionalConnections; i++) {
                        const sourceIdx = Math.floor(Math.random() * remainingNodes) + 1;
                        const targetIdx = Math.floor(Math.random() * remainingNodes) + 1;

                        if (sourceIdx !== targetIdx) {
                            const existingLink = mockLinks.find(link =>
                                (link.source === `mock_node${sourceIdx}` && link.target === `mock_node${targetIdx}`) ||
                                (link.source === `mock_node${targetIdx}` && link.target === `mock_node${sourceIdx}`)
                            );

                            if (!existingLink) {
                                mockLinks.push({
                                    source: `mock_node${sourceIdx}`,
                                    target: `mock_node${targetIdx}`,
                                    strength: Math.random() * 0.4 + 0.3
                                });
                            }
                        }
                    }

                    const filteredData = filterSuperstrings({
                        nodes: mockNodes,
                        links: mockLinks
                    });

                    const clusterResult = createClusterGroups(filteredData.nodes, filteredData.nodes, mobile);
                    setGraphData({
                        ...filteredData,
                        ...clusterResult
                    });

                    console.log(`Mock data generated: ${mockNodes.length} nodes, ${mockLinks.length} links`);
                } catch (error) {
                    console.error('Error generating mock data:', error);
                } finally {
                    setIsLoading(false);
                }
                return;
            }

            const maxMusicians = config.maxNodeLimit;
            if (initialMusicians.length > maxMusicians) {
                console.error(`Too many initial musicians. Maximum allowed is ${maxMusicians}.`);
                setIsLoading(false);
                alert(`Too many musicians selected. Please select a maximum of ${maxMusicians} musicians.`);
                return;
            }

            try {
                setIsLoading(true);

                let allNodes = [];
                let allLinks = [];

                const targetTotal = config.maxNodes;
                const numInitial = initialMusicians.length;
                const similarPerMusician = Math.max(2, Math.floor((targetTotal - numInitial)));

                if (initialMusicians.length === 1) {
                    const musician = initialMusicians[0];
                    const similarMusicians = await getSimilarMusicians(musician, config.maxNodes - 1);

                    const shuffled = similarMusicians.sort(() => 0.5 - Math.random());
                    const selectedMusicians = shuffled.slice(0, 5);

                    setClickedMusicians(selectedMusicians.map(m => m.name));

                    const selectedSimilarData = await Promise.all(
                        selectedMusicians.map(async (m) => {
                            const similar = await getSimilarMusicians(m.name, 6);
                            return {
                                source: m.name,
                                similar: similar
                            };
                        })
                    );

                    const newMusicians = [
                        musician,
                        ...similarMusicians.map(m => m.name),
                        ...selectedSimilarData.flatMap(data =>
                            data.similar.map(m => m.name)
                        )
                    ].filter(m => !processedMusicians.has(m));

                    const limitedMusicians = newMusicians.slice(0, config.maxNodeLimit);

                    const newNodes = await Promise.all(
                        limitedMusicians.map(m => fetchTagsForMusician(m))
                    );

                    const validNewNodes = newNodes.filter(node => node !== null && isValidNode(node));

                    const existingNodes = limitedMusicians
                        .filter(m => processedMusicians.has(m))
                        .map(m => graphData.nodes.find(n => n.id === m))
                        .filter(node => node !== undefined && isValidNode(node));

                    allNodes = [...validNewNodes, ...existingNodes];

                    allLinks = [
                        ...similarMusicians
                            .filter(artist => artist?.name && artist?.match)
                            .map(artist => ({
                                source: musician,
                                target: artist.name,
                                strength: parseFloat(artist.match) || 0
                            })),
                        ...selectedSimilarData.flatMap(data =>
                            data.similar
                                .filter(artist => artist?.name && artist?.match)
                                .map(artist => ({
                                    source: data.source,
                                    target: artist.name,
                                    strength: parseFloat(artist.match) || 0
                                }))
                        )
                    ];
                } else {
                    for (const musician of initialMusicians) {
                        const similarMusicians = await getSimilarMusicians(musician, similarPerMusician);

                        const newMusicians = [musician, ...similarMusicians.map(m => m.name)]
                            .filter(m => !processedMusicians.has(m));

                        const newNodes = await Promise.all(
                            newMusicians.map(m => fetchTagsForMusician(m))
                        );

                        const validNewNodes = newNodes.filter(node => node !== null && isValidNode(node));

                        const existingNodes = [musician, ...similarMusicians.map(m => m.name)]
                            .filter(m => processedMusicians.has(m))
                            .map(m => graphData.nodes.find(n => n.id === m))
                            .filter(node => node !== undefined && isValidNode(node));

                        allNodes = [...allNodes, ...validNewNodes, ...existingNodes];
                        allLinks = [...allLinks, ...similarMusicians
                            .filter(artist => artist?.name && artist?.match)
                            .map(artist => ({
                                source: musician,
                                target: artist.name,
                                strength: parseFloat(artist.match) || 0
                            }))];
                    }
                }

                if (allNodes.length > config.maxNodeLimit) {
                    allNodes = allNodes.slice(0, config.maxNodeLimit);
                }

                const uniqueNodes = Array.from(
                    new Set(allNodes.map(node => node.id))
                ).map(id => {
                    const node = allNodes.find(n => n.id === id);
                    return { id, tags: node.tags };
                });

                const filteredData = filterSuperstrings({
                    nodes: uniqueNodes,
                    links: allLinks
                });

                const clusterResult = createClusterGroups(filteredData.nodes, filteredData.nodes, mobile);
                setGraphData({
                    ...filteredData,
                    ...clusterResult
                });
            }
            catch (error) {
                console.error('Error fetching initial data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [initialMusicians, mobile]);

    // --- Data fetching: click-to-expand ---
    useEffect(() => {
        const fetchData = async () => {
            const latestClickedMusician = clickedMusicians[clickedMusicians.length - 1];
            if (!latestClickedMusician) return;

            const isMockNode = latestClickedMusician.startsWith('mock_');

            let similarMusicians;
            if (isMockNode) {
                similarMusicians = generateMockSimilarMusicians(latestClickedMusician, 6);
            } else {
                similarMusicians = await getSimilarMusicians(latestClickedMusician, 6);
            }

            const newMusicians = [latestClickedMusician, ...similarMusicians.map(m => m.name)]
                .filter(m => !processedMusicians.has(m));

            let newNodes;
            if (isMockNode) {
                newNodes = newMusicians.map(m => generateMockMusician(m));
            } else {
                newNodes = await Promise.all(
                    newMusicians.map(m => fetchTagsForMusician(m))
                );
            }

            const validNewNodes = newNodes.filter(node => node !== null && isValidNode(node));

            setGraphData(prev => {
                const filteredNewNodes = validNewNodes
                    .filter(node => !prev.nodes.some(n => n.id === node.id))
                    .map(node => ({
                        ...node,
                        x: dimensions.width / 2,
                        y: dimensions.height / 2
                    }));

                const newLinks = similarMusicians
                    .filter(artist => artist?.name && artist?.match)
                    .map(artist => ({
                        source: latestClickedMusician,
                        target: artist.name,
                        strength: parseFloat(artist.match) || 0
                    }));

                let combinedNodes = [...prev.nodes, ...filteredNewNodes];

                if (combinedNodes.length > config.maxNodeLimit) {
                    combinedNodes = combinedNodes.slice(0, config.maxNodeLimit);
                }

                const filteredData = filterSuperstrings({
                    nodes: combinedNodes,
                    links: [...prev.links, ...newLinks]
                });

                const clusterResult = createClusterGroups(filteredData.nodes, filteredData.nodes, mobile);
                return {
                    ...filteredData,
                    ...clusterResult
                };
            });
        };

        fetchData();
    }, [clickedMusicians, mobile]);

    const handleMusicianClick = (musician) => {
        setClickedMusicians(prev => [...prev, musician]);
    }

    // --- Mobile zoom ---
    const handleMobileZoomIn = useCallback(() => {
        if (!mobile || !zoomRef.current || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        const currentTransform = d3.zoomTransform(svg.node());
        const newScale = Math.min(currentTransform.k * 1.5, 5);

        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;

        const newTransform = d3.zoomIdentity
            .translate(centerX, centerY)
            .scale(newScale)
            .translate(-centerX, -centerY);

        svg.transition()
            .duration(300)
            .ease(d3.easeQuadOut)
            .call(zoomRef.current.transform, newTransform);

        setMobileZoomLevel(newScale);
    }, [mobile, dimensions]);

    const handleMobileZoomOut = useCallback(() => {
        if (!mobile || !zoomRef.current || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        const currentTransform = d3.zoomTransform(svg.node());
        const newScale = Math.max(currentTransform.k / 1.5, 0.5);

        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;

        const newTransform = d3.zoomIdentity
            .translate(centerX, centerY)
            .scale(newScale)
            .translate(-centerX, -centerY);

        svg.transition()
            .duration(300)
            .ease(d3.easeQuadOut)
            .call(zoomRef.current.transform, newTransform);

        setMobileZoomLevel(newScale);
    }, [mobile, dimensions]);

    const mobileZoomHandlers = useMemo(() => ({
        onZoomIn: handleMobileZoomIn,
        onZoomOut: handleMobileZoomOut
    }), [handleMobileZoomIn, handleMobileZoomOut]);

    useEffect(() => {
        if (onMobileZoomRequest) {
            onMobileZoomRequest(mobileZoomLevel, mobileZoomHandlers);
        }
    }, [onMobileZoomRequest, mobileZoomLevel, mobileZoomHandlers]);

    // --- D3 drag behavior ---
    const drag = (simulation) => {
        function dragstarted(event) {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
            if (!mobile) onMouseMove?.(event.sourceEvent);
        }

        function dragged(event) {
            event.subject.fx = event.x;
            event.subject.fy = event.y;
            if (!mobile) onMouseMove?.(event.sourceEvent);
        }

        function dragended(event) {
            if (!event.active) simulation.alphaTarget(0);
            event.subject.fx = null;
            event.subject.fy = null;
            if (!mobile) onMouseMove?.(event.sourceEvent);
        }

        return d3.drag()
            .on("start", dragstarted)
            .on("drag", dragged)
            .on("end", dragended);
    };

    const initialFontSize = config.initialFontSize;

    // Background follows zoom and pauses its drift while the graph is busy (dragging, zooming, simulation running)
    const activityRef = useRef({ simulation: false, zoom: false, paused: false });

    const syncActivity = () => {
        const activity = activityRef.current;
        const paused = activity.simulation || activity.zoom;
        if (paused !== activity.paused) {
            activity.paused = paused;
            onActivityChange?.(paused);
        }
    };

    const clearScheduled = () => {
        const scheduled = scheduledRef.current;
        if (scheduled.mouseMoveTimer) clearTimeout(scheduled.mouseMoveTimer);
        if (scheduled.zoomFrame !== null) cancelAnimationFrame(scheduled.zoomFrame);
        scheduled.mouseMoveTimer = null;
        scheduled.zoomFrame = null;
    };

    // --- Main graph creation ---
    const createMusicianGraph = () => {
        if (!graphData.nodes.length) return;

        // Stop previous simulation to free CPU
        if (simulationRef.current) {
            simulationRef.current.stop();
            simulationRef.current = null;
        }
        clearScheduled();
        updateClusterShapesRef.current = null;
        activityRef.current.simulation = false;
        activityRef.current.zoom = false;
        syncActivity();

        const width = dimensions.width;
        const height = dimensions.height;

        // Clusters are computed together with graphData; only fall back when missing
        const { clusterGroups, clusterLabels } = graphData.clusterGroups && graphData.clusterLabels
            ? graphData
            : createClusterGroups(graphData.nodes, graphData.nodes, mobile);

        d3.select(svgRef.current).selectAll("*").remove();

        const svg = d3.select(svgRef.current)
            .attr("viewBox", [0, 0, width, height])
            .attr("width", width)
            .attr("height", height);

        const container = svg.append("g");

        graphData.nodes.forEach(node => {
            if (node.x === undefined && node.y === undefined) {
                node.x = width / 2;
                node.y = height / 2;
            }
        });

        // Font size currently applied to each label, so the mouse effect only touches labels that change
        const labelFontSizes = new Map(graphData.nodes.map(n => [n.id, initialFontSize]));

        // Mouse move effects (desktop only)
        if (config.enableMouseEffects) {
            const minFontSize = initialFontSize;
            const maxFontSize = initialFontSize + 3;
            const maxDistance = 80;
            let pointer = null;

            svg.on('mousemove', function (event) {
                pointer = d3.pointer(event, this);
                if (scheduledRef.current.mouseMoveTimer) {
                    return;
                }

                scheduledRef.current.mouseMoveTimer = setTimeout(() => {
                    scheduledRef.current.mouseMoveTimer = null;

                    const transform = d3.zoomTransform(svg.node());
                    const transformedMouseX = (pointer[0] - transform.x) / transform.k;
                    const transformedMouseY = (pointer[1] - transform.y) / transform.k;

                    labelRects.each(function (d, i) {
                        const dx = d.x - transformedMouseX;
                        const dy = d.y - transformedMouseY;
                        const distance = Math.sqrt(dx * dx + dy * dy);

                        const rawFontSize = minFontSize + (maxFontSize - minFontSize) * (1 - Math.min(distance / maxDistance, 1));
                        const fontSize = Math.round(rawFontSize * 10) / 10;
                        if (labelFontSizes.get(d.id) === fontSize) return;
                        labelFontSizes.set(d.id, fontSize);

                        labelTextNodes[i].style.fontSize = `${fontSize}px`;

                        const labelWidth = getLabelWidth(d.id, fontSize) + 8;
                        const labelHeight = fontSize + 6;

                        d3.select(this)
                            .attr('width', labelWidth)
                            .attr('height', labelHeight)
                            .attr('x', -labelWidth / 2)
                            .attr('y', -labelHeight / 2);
                    });
                }, config.throttleDelay);
            });
        }

        // --- SVG defs: gradients and filters ---
        const defs = svg.append("defs");

        const gradientIds = {
            normal: "nodeGradient",
            initial: "initialNodeGradient",
            clicked: "clickedNodeGradient",
            hover: "hoverNodeGradient"
        };

        const nodeColors = config.nodeColors;

        if (!config.simplifiedVisuals) {
            // Desktop: Full gradient system
            const gradients = [
                { id: gradientIds.normal, colorIn: nodeColors.normal, colorOut: "#e6ab02" },
                { id: gradientIds.initial, colorIn: nodeColors.initial, colorOut: "#a6761d" },
                { id: gradientIds.clicked, colorIn: nodeColors.clicked, colorOut: "#666666" },
            ];

            gradients.forEach(({ id, colorIn, colorOut }) => {
                const gradient = defs.append("radialGradient")
                    .attr("id", id)
                    .attr("gradientUnits", "objectBoundingBox");
                gradient.append("stop")
                    .attr("offset", "0%")
                    .attr("stop-color", colorIn);
                gradient.append("stop")
                    .attr("offset", "100%")
                    .attr("stop-color", colorOut)
                    .attr("stop-opacity", 0.0);
            });

            const hoverGradient = defs.append("radialGradient")
                .attr("id", gradientIds.hover)
                .attr("gradientUnits", "objectBoundingBox");
            hoverGradient.append("stop")
                .attr("offset", "100%")
                .attr("stop-color", nodeColors.hover);
        }

        const getNodeFill = (d) => {
            if (config.simplifiedVisuals) {
                if (initialMusicians.includes(d.id)) return nodeColors.initial;
                if (clickedMusicians.includes(d.id)) return nodeColors.clicked;
                return nodeColors.normal;
            }
            if (initialMusicians.includes(d.id)) return `url(#${gradientIds.initial})`;
            if (clickedMusicians.includes(d.id)) return `url(#${gradientIds.clicked})`;
            return `url(#${gradientIds.normal})`;
        };

        const zoom = d3.zoom()
            .scaleExtent([0.5, 5])
            .touchable(true)
            .filter((event) => {
                if (mobile) {
                    return !event.ctrlKey && !event.button &&
                        (!event.touches || event.touches.length <= 1);
                }
                return !event.ctrlKey && !event.button;
            });

        zoomRef.current = zoom;
        svg.call(zoom);

        if (mobile) {
            svg
                .on('gesturestart', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                })
                .on('gesturechange', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                })
                .on('gestureend', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                });
        }

        const { opacityScale, clusterForceScale } = memoizedCalculations;

        const tagsGroupingForce = (alpha) => {
            const currentZoom = currentZoomTransform.current ? currentZoomTransform.current.k : 1;
            const forceStrength = clusterForceScale(currentZoom);

            clusterGroups.forEach(nodes => {
                if (nodes.length < 2) return;

                const centroidX = d3.mean(nodes, d => d.x);
                const centroidY = d3.mean(nodes, d => d.y);

                nodes.forEach(node => {
                    node.vy += (centroidY - node.y) * alpha * 1 * forceStrength;
                    node.vx += (centroidX - node.x) * alpha * 1 * forceStrength;
                });
            });
        };

        // Filters (disabled on mobile for performance)
        if (!config.disableFilters) {
            const filter = defs.append("filter")
                .attr("id", "feather")
                .attr("width", "200%")
                .attr("height", "200%")
                .attr("x", "-50%")
                .attr("y", "-50%");

            filter.append("feGaussianBlur")
                .attr("in", "SourceGraphic")
                .attr("stdDeviation", mobile ? "10" : "20")
                .attr("result", "blur");

            filter.append("feColorMatrix")
                .attr("in", "blur")
                .attr("mode", "matrix")
                .attr("values", `
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 2 0
    `);

            const textGlow = defs.append("filter")
                .attr("id", "text-glow")
                .attr("x", "-50%")
                .attr("y", "-50%")
                .attr("width", "200%")
                .attr("height", "200%");

            textGlow.append("feGaussianBlur")
                .attr("class", "blur")
                .attr("stdDeviation", mobile ? "1" : "2")
                .attr("result", "coloredBlur");

            const textGlowMerge = textGlow.append("feMerge");
            textGlowMerge.append("feMergeNode")
                .attr("in", "coloredBlur");
            textGlowMerge.append("feMergeNode")
                .attr("in", "SourceGraphic");

            const labelGlow = defs.append("filter")
                .attr("id", "label-glow")
                .attr("width", "200%")
                .attr("height", "200%")
                .attr("x", "-50%")
                .attr("y", "-50%");

            labelGlow.append("feGaussianBlur")
                .attr("in", "SourceGraphic")
                .attr("stdDeviation", mobile ? "0.5" : "1.5")
                .attr("result", "blur");

            labelGlow.append("feColorMatrix")
                .attr("in", "blur")
                .attr("mode", "matrix")
                .attr("values", `
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0
`);
        }

        // Validate links before creating simulation
        const nodeIdSet = new Set(graphData.nodes.map(n => n.id));
        const validLinks = graphData.links.filter(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            return nodeIdSet.has(sourceId) && nodeIdSet.has(targetId);
        });

        if (validLinks.length !== graphData.links.length) {
            console.warn(`Filtered out ${graphData.links.length - validLinks.length} invalid links`);
        }

        const simulation = d3.forceSimulation(graphData.nodes)
            .force("link", d3.forceLink(validLinks)
                .id(d => d.id)
                .distance(link => link.strength * (mobile ? 5 : 10))
                .strength(link => 0.1 + (link.strength * (mobile ? 0.5 : 0.9)))
            )
            .force("charge", d3.forceManyBody().strength(config.chargeStrength))
            .force("x", d3.forceX(width / 2).strength(config.forceStrength))
            .force("y", d3.forceY(height / 2).strength(config.forceStrength))
            .force("collision", d3.forceCollide().radius(mobile ? 40 : 30))
            .force("tagsGroup", tagsGroupingForce)
            .alpha(mobile ? 0.8 : 1)
            .alphaDecay(mobile ? 0.05 : 0.04)
            .restart();

        simulationRef.current = simulation;

        const link = container.append("g")
            .selectAll("line")
            .data(validLinks)
            .join("line")
            .attr("stroke", "#999")
            .attr("stroke-opacity", mobile ? 0.3 : 0.6)
            .attr("stroke-width", d => {
                if (mobile) {
                    return Math.min(d.strength * 2, 2);
                }
                return d.strength ** 1.6 * 5;
            });

        const node = container.append("g")
            .attr("class", "nodes")
            .selectAll("circle")
            .data(graphData.nodes)
            .join("circle")
            .attr("r", nodeSize)
            .attr("fill", getNodeFill)
            .attr("stroke", mobile ? "none" : null)
            .call(drag(simulation));

        const circleById = new Map();
        node.each(function (d) {
            circleById.set(d.id, this);
        });

        // Labels are split into a background layer and a text layer, each with a single shared glow
        // filter. One filter per layer is far cheaper to re-rasterize every tick than one per label.
        const labelBackgroundLayer = container.append("g")
            .attr("class", "label-backgrounds")
            .style("filter", config.disableFilters ? "none" : "url(#label-glow)");

        const labelTextLayer = container.append("g")
            .attr("class", "label-texts")
            .style("pointer-events", "none")
            .style("filter", config.disableFilters ? "none" : "url(#text-glow)");

        // Label backgrounds (also the click/drag/hover target)
        const labelRects = labelBackgroundLayer
            .selectAll("rect")
            .data(graphData.nodes)
            .join("rect")
            .attr("rx", mobile ? 4 : 6)
            .attr("ry", mobile ? 4 : 6)
            .attr("fill", mobile ? "rgba(19, 22, 36, 0.8)" : "rgba(19, 22, 36, 0.3)")
            .attr("width", d => getLabelWidth(d.id, initialFontSize) + 8)
            .attr("height", initialFontSize + 6)
            .attr("x", d => -(getLabelWidth(d.id, initialFontSize) + 8) / 2)
            .attr("y", -(initialFontSize + 6) / 2)
            .style("cursor", "pointer")
            .on("click", (event, d) => handleMusicianClick(d.id));

        // Label text
        const labelTexts = labelTextLayer
            .selectAll("text")
            .data(graphData.nodes)
            .join("text")
            .text(d => d.id)
            .attr("fill", "white")
            .attr("dy", "0.35em")
            .attr("text-anchor", "middle")
            .attr("text-rendering", "geometricPrecision")
            .style("font-family", '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif')
            .style("font-size", `${initialFontSize}px`)
            .style("font-weight", mobile ? "400" : "500");

        const labelTextNodes = labelTexts.nodes();

        // Hover effects (desktop only)
        if (config.enableMouseEffects) {
            labelRects
                .on("mouseover", function (event, d) {
                    // Let the next mouse-move pass re-apply this label's size, as before
                    labelFontSizes.delete(d.id);

                    d3.select(this)
                        .attr("width", d => getLabelWidth(d.id, initialFontSize * 1.2) + 12)
                        .attr("height", initialFontSize * 1.2 + 8)
                        .attr("x", d => -(getLabelWidth(d.id, initialFontSize * 1.2) + 12) / 2)
                        .attr("y", -(initialFontSize * 1.2 + 8) / 2);

                    d3.select(circleById.get(d.id))
                        .transition()
                        .duration(300)
                        .ease(d3.easeBackOut.overshoot(2))
                        .attr("r", nodeSize * 1.2)
                        .attr("stroke", "#fff")
                        .attr("stroke-width", 1.5)
                        .attr("stroke-opacity", 0.4);
                })
                .on("mouseout", function (event, d) {
                    labelFontSizes.delete(d.id);

                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr("width", d => getLabelWidth(d.id, initialFontSize) + 8)
                        .attr("height", initialFontSize + 6)
                        .attr("x", d => -(getLabelWidth(d.id, initialFontSize) + 8) / 2)
                        .attr("y", -(initialFontSize + 6) / 2);

                    d3.select(circleById.get(d.id))
                        .interrupt()
                        .attr("r", nodeSize)
                        .attr("fill", getNodeFill)
                        .attr("stroke", null)
                        .attr("stroke-width", null);
                });
        }

        labelRects.call(drag(simulation));

        // Cluster overlays
        const clusterOverlayGroup = container.append("g")
            .attr("class", "cluster-overlays")
            .style("opacity", 0);

        const clusterOverlays = clusterOverlayGroup
            .selectAll("path")
            .data(Array.from(clusterGroups.entries()))
            .join("path")
            .attr("fill", (d, i) => d3.schemeTableau10[i % 10])
            .attr("fill-opacity", 0.2)
            .attr("stroke", "none")
            .attr("pointer-events", "none")
            .attr("filter", config.disableFilters ? "none" : "url(#feather)");

        // Cluster labels
        const clusterLabelGroup = container.append("g")
            .attr("class", "cluster-labels")
            .style("opacity", 0);

        const clusterLabelElements = clusterLabelGroup
            .selectAll("g")
            .data(Array.from(clusterGroups.entries()))
            .join("g")
            .attr("pointer-events", "none");

        if (mobile) {
            clusterLabelElements.append("circle")
                .attr("r", 25)
                .attr("fill", (d, i) => d3.schemeTableau10[i % 10])
                .attr("fill-opacity", 0.1);
        } else {
            clusterLabelElements.append("path")
                .attr("d", () => {
                    const points = [];
                    const numPoints = 8;
                    const radius = 40;

                    for (let i = 0; i < numPoints; i++) {
                        const angle = (i / numPoints) * 2 * Math.PI;
                        const variance = Math.random() * 15;
                        const x = Math.cos(angle) * (radius + variance);
                        const y = Math.sin(angle) * (radius + variance);
                        points.push([x, y]);
                    }

                    return d3.line()
                        .x(d => d[0])
                        .y(d => d[1])
                        .curve(d3.curveBasisClosed)(points);
                })
                .attr("fill", (d, i) => d3.schemeTableau10[i % 10])
                .attr("fill-opacity", 0.2)
                .attr("filter", "url(#feather)");
        }

        clusterLabelElements.append("text")
            .attr("fill", (d, i) => d3.schemeTableau10[i % 10])
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .attr("text-rendering", "geometricPrecision")
            .style("font-size", mobile ? "16px" : "24px")
            .style("font-family", '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif')
            .style("font-weight", mobile ? "600" : "bold")
            .attr("filter", config.disableFilters ? "none" : "url(#text-glow)")
            .text((d) => clusterLabels.get(d[0]));

        // Hull paths and cluster label positions; skipped on ticks while the overlays are fully transparent
        const updateClusterShapes = () => {
            clusterOverlays
                .attr("d", ([cluster, nodes]) => generateHullPath(nodes, mobile));

            clusterLabelElements
                .attr("transform", ([cluster, nodes]) => {
                    const centroidX = d3.mean(nodes, d => d.x);
                    const centroidY = d3.mean(nodes, d => d.y);
                    return `translate(${centroidX},${centroidY - 50})`;
                });
        };
        updateClusterShapesRef.current = updateClusterShapes;

        // Crossfade between musician labels and cluster overlays; only writes styles when values change
        let labelOpacity = null;
        let clusterShapesVisible = true;

        const applyZoomOpacity = (scale) => {
            const nextLabelOpacity = opacityScale(scale);
            if (nextLabelOpacity === labelOpacity) return;
            labelOpacity = nextLabelOpacity;

            labelBackgroundLayer.style('opacity', labelOpacity);
            labelTextLayer.style('opacity', labelOpacity);

            const overlayOpacity = 1 - labelOpacity;
            clusterOverlayGroup.style('opacity', overlayOpacity);
            clusterLabelGroup.style('opacity', overlayOpacity);

            const wasVisible = clusterShapesVisible;
            clusterShapesVisible = overlayOpacity > 0;
            if (clusterShapesVisible && !wasVisible) updateClusterShapes();
        };

        const applyZoomFrame = () => {
            scheduledRef.current.zoomFrame = null;
            const transform = currentZoomTransform.current;
            const scale = transform.k;

            if (!mobile) {
                // Nudge a settled simulation when zoomed out, instead of restarting it on every zoom event
                if (scale < 0.8 && simulation.alpha() < simulation.alphaMin()) {
                    simulation.alpha(0.05).restart();
                }

                const centerX = width / 2;
                const centerY = height / 2;
                const adjustedX = (transform.x - centerX * (1 - scale)) / scale;
                const adjustedY = (transform.y - centerY * (1 - scale)) / scale;
                onZoomTransform?.(adjustedX, adjustedY, scale);
            }

            applyZoomOpacity(scale);
        };

        zoom
            .on('start', () => {
                activityRef.current.zoom = true;
                syncActivity();
            })
            .on('zoom', (event) => {
                container.attr('transform', event.transform);
                currentZoomTransform.current = event.transform;

                // Coalesce zoom events (wheel and touch can fire faster than the display refreshes)
                if (scheduledRef.current.zoomFrame === null) {
                    scheduledRef.current.zoomFrame = requestAnimationFrame(applyZoomFrame);
                }
            })
            .on('end', () => {
                activityRef.current.zoom = false;
                syncActivity();

                // Mobile zoom buttons only need the level once a gesture finishes, not on every frame
                if (mobile) setMobileZoomLevel(currentZoomTransform.current.k);
            });

        svg.call(zoom.transform, currentZoomTransform.current);
        // Apply the initial crossfade synchronously so labels don't flash at full opacity for a frame
        if (scheduledRef.current.zoomFrame !== null) {
            cancelAnimationFrame(scheduledRef.current.zoomFrame);
        }
        applyZoomFrame();

        simulation.on("tick", () => {
            if (!activityRef.current.simulation) {
                activityRef.current.simulation = true;
                syncActivity();
            }

            link
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);

            node
                .attr("cx", d => d.x)
                .attr("cy", d => d.y);

            labelRects
                .attr("transform", d => `translate(${d.x},${d.y})`);
            labelTexts
                .attr("transform", d => `translate(${d.x},${d.y})`);

            if (clusterShapesVisible) updateClusterShapes();
        });

        simulation.on("end", () => {
            activityRef.current.simulation = false;
            syncActivity();
        });
    };

    useEffect(() => {
        createMusicianGraph();
    }, [graphData, dimensions]);

    // Stop the simulation and pending work on unmount
    useEffect(() => {
        const activity = activityRef.current;
        return () => {
            if (simulationRef.current) simulationRef.current.stop();
            clearScheduled();
            activity.simulation = false;
            activity.zoom = false;
            syncActivity();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        if (mobile) {
            const originalViewport = document.querySelector('meta[name="viewport"]');
            const originalContent = originalViewport ? originalViewport.content : '';

            if (originalViewport) {
                originalViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
            }

            const preventMultiTouch = (e) => {
                if (e.touches && e.touches.length > 1) {
                    e.preventDefault();
                }
            };

            const preventGestures = (e) => {
                e.preventDefault();
            };

            document.addEventListener('touchstart', preventMultiTouch, { passive: false });
            document.addEventListener('touchmove', preventMultiTouch, { passive: false });
            document.addEventListener('gesturestart', preventGestures, { passive: false });
            document.addEventListener('gesturechange', preventGestures, { passive: false });
            document.addEventListener('gestureend', preventGestures, { passive: false });

            return () => {
                window.removeEventListener('resize', handleResize);

                if (originalViewport && originalContent) {
                    originalViewport.content = originalContent;
                }

                document.removeEventListener('touchstart', preventMultiTouch);
                document.removeEventListener('touchmove', preventMultiTouch);
                document.removeEventListener('gesturestart', preventGestures);
                document.removeEventListener('gesturechange', preventGestures);
                document.removeEventListener('gestureend', preventGestures);
            };
        }

        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize, mobile]);

    return (
        <>
            {isLoading && <LoadingOverlay />}

            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                style={{
                    ...graphStyle,
                    width: dimensions.width,
                    height: dimensions.height
                }}
                onMouseMove={mobile ? undefined : onMouseMove}
                onTouchStart={mobile ? (e) => {
                    if (e.touches.length > 1) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } : undefined}
                onTouchMove={mobile ? (e) => {
                    if (e.touches.length > 1) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } : undefined}
            />
        </>
    );
}
